/**
 * Cluster — the multicellular player organism.
 *
 * The cluster spawns as a 4-cell ring (no center), and grows in
 * concentric rings (8, 12, 16, 20) as biomass thresholds are
 * crossed. Each ring fills via a max-min-distance permutation so
 * partial rings stay visually balanced rather than one-sided.
 *
 * Movement is mouse-follow with momentum (heavier than a single
 * cell). Motor cells multiply pull and the speed cap; sensor cells
 * widen the absorption sweep; armor cells roll on every predator
 * hit to fully negate it.
 *
 * Active ability: dash. A keypress slams an impulse toward the
 * cursor and grants brief invulnerability; the cluster can ram
 * predators while dashing instead of just dodging them.
 */

import { Container, Graphics } from 'pixi.js';
import { drawCellBody } from '@/visuals/bio';

export type CellType = 'core' | 'armor' | 'motor' | 'sensor';

export interface ClusterCell {
  /** Concentric ring this cell lives on (0 = innermost). */
  ring: number;
  /** Slot index within the ring (0..ringSize-1). */
  slot: number;
  /** Base offset from cluster center. Wobble adds to this each frame. */
  baseOx: number;
  baseOy: number;
  /** Last drawn offset including wobble. Used by strand drawing. */
  ox: number;
  oy: number;
  /** Per-cell oscillation phase for wobble + body time variation. */
  phase: number;
  /** Per-cell graphics — drawCellBody re-clears each frame. */
  g: Graphics;
  /** Rendered radius. Constant for now; later slices vary by cell type. */
  radius: number;
  /** Specialization. Drives both stats and the cell badge overlay. */
  type: CellType;
}

// Per-cell stat contributions. Tuned so 5 specialists make a noticeable
// difference but no single type trivializes the game on its own.
export const MOTOR_SPEED_PER_CELL = 0.06;
export const SENSOR_REACH_PER_CELL = 0.05;
export const ARMOR_NEGATE_PER_CELL = 0.08;
export const ARMOR_NEGATE_CAP = 0.75;

const CELL_RADIUS = 14;
// Slightly under 2× radius so neighbors overlap and read as "stuck
// together" rather than "drifting near each other".
const NEIGHBOR_GAP = CELL_RADIUS * 1.55;

/**
 * Ring layout. Index = ring number; value = how many cell slots
 * sit on that ring. Ring 0 = 4 spawn cells; subsequent rings grow
 * by 4 each, which keeps the silhouette filling outward roughly
 * uniformly. Five rings cover the 14 growth thresholds with room
 * to spare.
 */
const RING_SIZES = [4, 8, 12, 16, 20];
/** Cluster cell count at which the "fully evolved" golden glow lights
 *  up. Matches the multicell stage's GROWTH_THRESHOLDS.length + 4
 *  spawn cells; kept in sync by hand. */
export const FULL_GROWN_CELLS = 26;
/** Radius of each ring as a multiple of NEIGHBOR_GAP. Stepped so
 *  cells in adjacent rings overlap at their nearest pair. */
const RING_RADII = [0.78, 1.78, 2.78, 3.78, 4.78];
/** Half-slot rotation between odd/even rings — makes adjacent rings
 *  hex-pack instead of stacking radially. */
const RING_ANGLE_OFFSETS = [0, 0.5, 0, 0.5, 0];

/**
 * Pre-compute the (ring, slot) sequence the addCell stream should
 * follow. Within each ring, slots are visited in a max-min-distance
 * order so a partially-filled ring still looks balanced. Computed
 * once at module load.
 */
const FILL_ORDER: Array<[number, number]> = [];
{
  for (let r = 0; r < RING_SIZES.length; r++) {
    const N = RING_SIZES[r];
    const filled: number[] = [];
    for (let k = 0; k < N; k++) {
      if (filled.length === 0) {
        filled.push(0);
        FILL_ORDER.push([r, 0]);
        continue;
      }
      let bestSlot = -1;
      let bestMin = -1;
      for (let s = 0; s < N; s++) {
        if (filled.indexOf(s) !== -1) continue;
        let minD = N;
        for (const f of filled) {
          const raw = Math.abs(s - f);
          const d = Math.min(raw, N - raw);
          if (d < minD) minD = d;
        }
        if (minD > bestMin) { bestMin = minD; bestSlot = s; }
      }
      filled.push(bestSlot);
      FILL_ORDER.push([r, bestSlot]);
    }
  }
}

function ringSlotPosition(ring: number, slot: number): [number, number] {
  const N = RING_SIZES[ring];
  const r = RING_RADII[ring] * NEIGHBOR_GAP;
  const offsetTurns = RING_ANGLE_OFFSETS[ring] / N;
  const a = (slot / N + offsetTurns) * Math.PI * 2;
  return [Math.cos(a) * r, Math.sin(a) * r];
}

export class Cluster {
  readonly container: Container;
  readonly cells: ClusterCell[];
  /** World position of the cluster centroid. */
  x: number;
  y: number;
  /** Current velocity. Read by future systems for damage knockback etc. */
  vx = 0;
  vy = 0;

  /** Membrane glow drawn behind the cells so the cluster reads as one body. */
  private envelope: Graphics;
  /** Cytoplasmic strands drawn between adjacent cells. */
  private strands: Graphics;
  private bodyTime = 0;

  /** Invulnerability window after a hit. */
  iframeT = 0;
  /** Active dash duration (>0 → dash trail + i-frames). */
  dashActiveT = 0;
  /** Cooldown until another dash can fire. */
  dashCooldownT = 0;
  /** Direction unit vector of the in-progress dash. Used by the trail FX. */
  private dashDirX = 0;
  private dashDirY = 0;

  constructor(parent: Container, startX: number, startY: number) {
    this.container = new Container();
    this.x = startX;
    this.y = startY;
    parent.addChild(this.container);

    this.envelope = new Graphics();
    this.container.addChild(this.envelope);
    this.strands = new Graphics();
    this.container.addChild(this.strands);

    // Spawn = ring 0 (4 cells). The 4 slots are filled in a balanced
    // order so the cluster reads as symmetric on frame one.
    this.cells = [];
    for (let i = 0; i < RING_SIZES[0]; i++) {
      const [ring, slot] = FILL_ORDER[i];
      const [ox, oy] = ringSlotPosition(ring, slot);
      const g = new Graphics();
      this.container.addChild(g);
      this.cells.push({
        ring, slot, baseOx: ox, baseOy: oy, ox, oy,
        phase: i * 1.273,
        g, radius: CELL_RADIUS, type: 'core',
      });
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }

  /**
   * Append a cell at the next slot in the fill order. Since the
   * order is precomputed and cells are stored in append order, the
   * Nth cell always lives at FILL_ORDER[N], including the spawn
   * cells at indices 0..3.
   */
  addCell(type: CellType = 'core'): void {
    const idx = this.cells.length;
    if (idx >= FILL_ORDER.length) return; // ring 4 full — silently no-op
    const [ring, slot] = FILL_ORDER[idx];
    const [ox, oy] = ringSlotPosition(ring, slot);
    const g = new Graphics();
    this.container.addChild(g);
    this.cells.push({
      ring, slot, baseOx: ox, baseOy: oy, ox, oy,
      phase: idx * 1.273,
      g, radius: CELL_RADIUS, type,
    });
  }

  countOf(type: CellType): number {
    let n = 0;
    for (const c of this.cells) if (c.type === type) n++;
    return n;
  }

  get speedMult(): number {
    return 1 + this.countOf('motor') * MOTOR_SPEED_PER_CELL;
  }

  get hitNegateChance(): number {
    return Math.min(ARMOR_NEGATE_CAP, this.countOf('armor') * ARMOR_NEGATE_PER_CELL);
  }

  /**
   * Pop the most recently added cell. Returns true if a cell was
   * removed, false if the cluster was already empty. Last-added is
   * always the outermost slot in the fill order, so the silhouette
   * shrinks from the outside in.
   */
  removeCell(): boolean {
    const c = this.cells.pop();
    if (!c) return false;
    c.g.destroy();
    return true;
  }

  applyKnockback(fromX: number, fromY: number, force: number): void {
    const dx = this.x - fromX;
    const dy = this.y - fromY;
    const d = Math.hypot(dx, dy) || 1;
    this.vx += (dx / d) * force;
    this.vy += (dy / d) * force;
  }

  /**
   * Pinball-style bounce: reflect velocity across the surface normal
   * with elastic restitution, then add an outward kick so the cluster
   * always separates from the impact point. Used for solid shards
   * where the hit should feel like a "thwock" instead of a soft push.
   * Predators keep applyKnockback — they're squishy and don't reflect
   * incoming motion the way a rock does.
   */
  applyBounce(fromX: number, fromY: number, restitution: number, kick: number): void {
    const dx = this.x - fromX;
    const dy = this.y - fromY;
    const d = Math.hypot(dx, dy) || 1;
    const nx = dx / d;
    const ny = dy / d;
    const vDotN = this.vx * nx + this.vy * ny;
    // Only reflect when moving INTO the rock. Otherwise we'd add
    // velocity to a cluster that's already escaping the contact.
    if (vDotN < 0) {
      this.vx -= (1 + restitution) * vDotN * nx;
      this.vy -= (1 + restitution) * vDotN * ny;
    }
    this.vx += nx * kick;
    this.vy += ny * kick;
  }

  /**
   * Dash toward a world-space target. Adds a big velocity impulse
   * in that direction, brief i-frames so the player can charge
   * through a homing predator, and a cooldown so it can't be spammed.
   * Returns true if the dash fired.
   */
  tryDash(targetX: number, targetY: number): boolean {
    if (this.dashCooldownT > 0) return false;
    const dx = targetX - this.x;
    const dy = targetY - this.y;
    const d = Math.hypot(dx, dy);
    if (d < 1) return false; // no direction → don't waste the cooldown
    this.dashDirX = dx / d;
    this.dashDirY = dy / d;
    const force = 820 * this.speedMult;
    this.vx += this.dashDirX * force;
    this.vy += this.dashDirY * force;
    this.dashCooldownT = 0.85;
    this.dashActiveT = 0.22;
    // I-frames during dash so a charge through a predator doesn't
    // cost a cell — that's the whole point of the ability.
    this.iframeT = Math.max(this.iframeT, 0.30);
    return true;
  }

  get absorptionRadius(): number {
    const n = this.cells.length;
    const sensorMult = 1 + this.countOf('sensor') * SENSOR_REACH_PER_CELL;
    return CELL_RADIUS * (1.6 + Math.sqrt(n) * 0.55) * sensorMult;
  }

  update(dt: number, mouseX: number, mouseY: number): void {
    this.bodyTime += dt;
    if (this.iframeT > 0) this.iframeT = Math.max(0, this.iframeT - dt);
    if (this.dashActiveT > 0) this.dashActiveT = Math.max(0, this.dashActiveT - dt);
    if (this.dashCooldownT > 0) this.dashCooldownT = Math.max(0, this.dashCooldownT - dt);

    const dx = mouseX - this.x;
    const dy = mouseY - this.y;
    const sm = this.speedMult;
    const pull = 4.0 * sm;
    this.vx += dx * pull * dt;
    this.vy += dy * pull * dt;
    const friction = Math.pow(0.04, dt);
    this.vx *= friction;
    this.vy *= friction;
    // Symmetric speed cap. Per-axis caps (X<Y) made horizontal camera
    // scroll feel like a slow drift instead of a quick "whoosh" —
    // food appeared to track the cluster only on horizontal pulls.
    // With both axes at the same cap, the camera-window engagement
    // and follow rate match on both axes; dash temporarily lifts the
    // cap so the impulse actually translates into motion before
    // friction trims it back.
    const dashScale = this.dashActiveT > 0 ? 2.4 : 1.0;
    const maxSpeedX = 340 * sm * dashScale;
    const maxSpeedY = 340 * sm * dashScale;
    if (this.vx > maxSpeedX) this.vx = maxSpeedX;
    else if (this.vx < -maxSpeedX) this.vx = -maxSpeedX;
    if (this.vy > maxSpeedY) this.vy = maxSpeedY;
    else if (this.vy < -maxSpeedY) this.vy = -maxSpeedY;
    this.x += this.vx * dt;
    this.y += this.vy * dt;

    this.draw();
  }

  private draw(): void {
    this.container.position.set(this.x, this.y);

    const t = this.bodyTime;
    const reach = this.absorptionRadius;
    const hurt = this.iframeT > 0;
    const blink = hurt ? 0.5 + 0.5 * Math.sin(t * 80) : 1;
    const dashing = this.dashActiveT > 0;

    // Cluster-wide breathe — a slow uniform inflate/deflate that
    // gives the whole body a respiratory feel even when nothing is
    // moving on-screen. Applied as a multiplier to per-cell wobble
    // amplitudes, not the offsets themselves, so the silhouette
    // oscillates rather than the cells drifting in unison.
    const breathe = 1 + Math.sin(t * 1.1) * 0.04;

    // Update each cell's drawn offset with a small organic wobble
    // around its ring position. Per-cell phase keeps neighbours out
    // of sync — without that the wobble would look like a single
    // global oscillation.
    for (const c of this.cells) {
      const wob = NEIGHBOR_GAP * 0.06 * breathe;
      c.ox = c.baseOx + Math.sin(t * 1.7 + c.phase) * wob;
      c.oy = c.baseOy + Math.cos(t * 1.4 + c.phase * 1.3) * wob;
    }

    // --- envelope: wobbly polygon (was: stacked circles) ---
    // Polygon with sine-modulated radius gives the membrane a living
    // rim instead of a static disc. Multiple low-alpha layers stack
    // for the soft halo.
    this.envelope.clear();
    const envSamples = 36;
    const fullyGrown = this.cells.length >= FULL_GROWN_CELLS;
    const envColor = hurt && blink > 0.5 ? 0xff5566 : 0x4FD1C5;
    for (let layer = 0; layer < 4; layer++) {
      const layerR = reach * (1 + layer * 0.09) * breathe;
      const pts: number[] = [];
      for (let i = 0; i < envSamples; i++) {
        const a = (i / envSamples) * Math.PI * 2;
        const wob =
          Math.sin(a * 3 + t * 0.8 + layer) * 0.045 +
          Math.cos(a * 5 - t * 0.55 + layer * 0.7) * 0.03;
        const r = layerR * (1 + wob);
        pts.push(Math.cos(a) * r, Math.sin(a) * r);
      }
      const a = 0.16 - layer * 0.04;
      this.envelope.poly(pts).fill({ color: envColor, alpha: a });
    }
    // Apex-form aura. Once the cluster reaches full size, layer a
    // pulsing golden corona over the normal envelope so the player
    // can see at a glance that they're maxed out. Two layers — a
    // wide soft outer halo and a tighter ring — give the glow depth
    // without obscuring the body.
    if (fullyGrown && !hurt) {
      const auraPulse = 1 + Math.sin(t * 1.6) * 0.06;
      const auraR0 = reach * 1.32 * auraPulse;
      const auraR1 = reach * 1.16 * auraPulse;
      const outerPts: number[] = [];
      const innerPts: number[] = [];
      for (let i = 0; i < envSamples; i++) {
        const a = (i / envSamples) * Math.PI * 2;
        const wob = Math.sin(a * 4 + t * 1.1) * 0.03;
        outerPts.push(Math.cos(a) * auraR0 * (1 + wob), Math.sin(a) * auraR0 * (1 + wob));
        innerPts.push(Math.cos(a) * auraR1 * (1 + wob), Math.sin(a) * auraR1 * (1 + wob));
      }
      this.envelope.poly(outerPts).fill({ color: 0xffd166, alpha: 0.10 + 0.04 * Math.sin(t * 1.6) });
      this.envelope.poly(innerPts).fill({ color: 0xfff1c1, alpha: 0.16 + 0.05 * Math.sin(t * 1.6 + 0.7) });
    }
    // Dash trail: a leading streak in the dash direction, fades with
    // the remaining dash duration.
    if (dashing) {
      const k = this.dashActiveT / 0.22;
      const tx = this.dashDirX * reach * 1.4;
      const ty = this.dashDirY * reach * 1.4;
      this.envelope
        .ellipse(tx * 0.5, ty * 0.5, reach * 1.2, reach * 0.45)
        .fill({ color: 0x76e4f7, alpha: 0.16 * k });
      this.envelope
        .circle(-tx * 0.4, -ty * 0.4, reach * 0.5)
        .fill({ color: 0xC4FAF0, alpha: 0.30 * k });
    }

    // --- cytoplasmic strands ---
    // Soft cyan curves between each pair of cells closer than the
    // strand threshold. Sells "these cells are bound into one body"
    // far better than the envelope alone.
    this.strands.clear();
    const strandThreshSq = (NEIGHBOR_GAP * 1.4) * (NEIGHBOR_GAP * 1.4);
    const strandColor = hurt && blink > 0.5 ? 0xff8888 : 0x4FD1C5;
    for (let i = 0; i < this.cells.length; i++) {
      const a = this.cells[i];
      for (let j = i + 1; j < this.cells.length; j++) {
        const b = this.cells[j];
        const dx2 = a.ox - b.ox;
        const dy2 = a.oy - b.oy;
        const d2 = dx2 * dx2 + dy2 * dy2;
        if (d2 > strandThreshSq) continue;
        // Quadratic bezier-ish: midpoint nudged by a perpendicular
        // sine wobble so the strand looks alive, not segment-straight.
        const mx = (a.ox + b.ox) * 0.5;
        const my = (a.oy + b.oy) * 0.5;
        const len = Math.sqrt(d2) || 1;
        const nx = -(b.oy - a.oy) / len;
        const ny = (b.ox - a.ox) / len;
        const wob = Math.sin(t * 2.2 + (i + j) * 0.7) * NEIGHBOR_GAP * 0.10;
        const cx = mx + nx * wob;
        const cy = my + ny * wob;
        // Sample the curve in segments and stroke.
        const segs = 6;
        let prevX = a.ox;
        let prevY = a.oy;
        for (let s = 1; s <= segs; s++) {
          const u = s / segs;
          const ux = (1 - u) * (1 - u) * a.ox + 2 * (1 - u) * u * cx + u * u * b.ox;
          const uy = (1 - u) * (1 - u) * a.oy + 2 * (1 - u) * u * cy + u * u * b.oy;
          this.strands
            .moveTo(prevX, prevY)
            .lineTo(ux, uy)
            .stroke({ color: strandColor, alpha: 0.40, width: 4 });
          this.strands
            .moveTo(prevX, prevY)
            .lineTo(ux, uy)
            .stroke({ color: 0xC4FAF0, alpha: 0.18, width: 2 });
          prevX = ux;
          prevY = uy;
        }
      }
    }

    // --- per-cell rendering ---
    for (const c of this.cells) {
      c.g.position.set(c.ox, c.oy);
      // Per-cell time offset so the membrane wobble doesn't pulse
      // in lockstep across cells. drawCellBody is animation-driven
      // by `time`, so a phase shift is enough to desync them.
      drawCellBody(
        c.g,
        { radius: c.radius, fieldRadius: c.radius * 1.2 },
        t + c.phase * 0.4,
        [],
      );
      if (c.type !== 'core') drawCellTypeBadge(c.g, c.radius, c.type, t + c.phase);
    }
  }
}

/**
 * Small per-type cosmetic painted on top of drawCellBody. Intentionally
 * subtle — at a glance the cluster reads as one body, but with attention
 * the player can pick out which cells are which.
 */
function drawCellTypeBadge(g: Graphics, radius: number, type: CellType, time: number): void {
  if (type === 'armor') {
    g.circle(0, 0, radius * 1.10).stroke({ color: 0x6b46c1, alpha: 0.85, width: 2 });
    g.circle(0, 0, radius * 1.16).stroke({ color: 0xa78bfa, alpha: 0.4, width: 1 });
    const sides = 6;
    for (let i = 0; i < sides; i++) {
      const a = (i / sides) * Math.PI * 2 + time * 0.05;
      g.moveTo(Math.cos(a) * radius * 1.10, Math.sin(a) * radius * 1.10)
        .lineTo(Math.cos(a) * radius * 0.95, Math.sin(a) * radius * 0.95)
        .stroke({ color: 0x6b46c1, alpha: 0.55, width: 1 });
    }
  } else if (type === 'motor') {
    const t = time * 6;
    const segs = 8;
    let prevX = -radius * 1.0;
    let prevY = 0;
    for (let i = 1; i <= segs; i++) {
      const f = i / segs;
      const x = -radius * (1.0 + f * 1.4);
      const y = Math.sin(t + f * 6) * radius * 0.18 * f;
      g.moveTo(prevX, prevY).lineTo(x, y).stroke({ color: 0xfde68a, alpha: 0.7, width: 1.4 });
      prevX = x; prevY = y;
    }
    g.circle(-radius * 1.0, 0, radius * 0.18).fill({ color: 0xfaf089, alpha: 0.7 });
  } else if (type === 'sensor') {
    const pulse = (time * 1.4) % 1.0;
    g.circle(0, 0, radius * 1.05).stroke({ color: 0x76e4f7, alpha: 0.85, width: 1.5 });
    const r = radius * (1.10 + pulse * 0.6);
    const a = 0.55 * (1 - pulse);
    g.circle(0, 0, r).stroke({ color: 0xC4FAF0, alpha: a, width: 1.5 });
  }
}
